import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { X } from 'lucide-react';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { TextareaInput } from '@components/atoms/TextareaInput';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { Button } from '@components/atoms/Button';
import { FileDropZone } from '@components/molecules/FileDropZone';
import { useTasks } from '@hooks/api/task/useTasks';
import { useProjects } from '@hooks/api';
import { useCreateIncident } from '@hooks/api/incident/useCreateIncident';
import { DelayUnitOfMeasure, IncidentType } from '@models/incident.model';
import { useTranslation } from '@hooks/useTranslation';

export interface ReportIncidentModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * When set (project view entry point), the project is fixed and shown read-only.
   * When unset (/incidents entry point), the user picks a project from a dropdown.
   */
  projectId?: string;
  projectName?: string;
  onSuccess?: () => void;
}

interface ReportIncidentFormValues {
  projectId: string;
  name: string;
  incidentTime: string;
  incidentType: string;
  otherIncidentType: string;
  delay: string;
  delayUnitOfMeasure: string;
  description: string;
  taskIds: string[];
}

// `datetime-local` wants "YYYY-MM-DDTHH:mm" in LOCAL wall-clock time (no offset) — same
// convention as `EditSessionExitModal`'s `toLocalInputValue`/`parseLocalInput`.
const toLocalInputValue = (d: Date): string => {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const parseLocalInputToIso = (value: string): string | undefined => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return undefined;
  const [, year, month, day, hour, minute] = match;
  const parsed = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );
  return isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

const buildIncidentTypeOptions = (t: any): SelectOption[] => [
  { value: IncidentType.WEATHER, label: t?.typeWeather || 'Weather' },
  { value: IncidentType.ACCIDENT, label: t?.typeAccident || 'Accident' },
  {
    value: IncidentType.MATERIALS_ISSUE,
    label: t?.typeMaterialsIssue || 'Materials Issue',
  },
  {
    value: IncidentType.EQUIPMENT_ISSUE,
    label: t?.typeEquipmentIssue || 'Equipment Issue',
  },
  { value: IncidentType.OTHER, label: t?.typeOther || 'Other' },
];

const buildDelayUnitOptions = (t: any): SelectOption[] => [
  { value: DelayUnitOfMeasure.HOURS, label: t?.unitHours || 'Hours' },
  { value: DelayUnitOfMeasure.DAYS, label: t?.unitDays || 'Days' },
  { value: DelayUnitOfMeasure.WEEKS, label: t?.unitWeeks || 'Weeks' },
];

const buildValidationSchema = (requireProjectSelect: boolean, t: any) =>
  Yup.object({
    projectId: requireProjectSelect
      ? Yup.string().required(t?.projectRequired || 'Project is required')
      : Yup.string(),
    name: Yup.string().required(t?.nameRequired || 'Incident name is required'),
    incidentTime: Yup.string().required(
      t?.timeRequired || 'Time of incident is required',
    ),
    incidentType: Yup.string().required(t?.typeRequired || 'Incident type is required'),
    otherIncidentType: Yup.string().when('incidentType', {
      is: IncidentType.OTHER,
      then: (schema) =>
        schema.required(t?.otherTypeRequired || 'Please specify the incident type'),
      otherwise: (schema) => schema,
    }),
    delay: Yup.number()
      .typeError(t?.delayMustBeNumber || 'Delay must be a number')
      .min(0, t?.delayMustBePositive || 'Delay must be positive')
      .required(t?.delayRequired || 'Delay is required'),
    delayUnitOfMeasure: Yup.string().required(
      t?.delayUnitRequired || 'Delay unit is required',
    ),
    description: Yup.string().required(
      t?.descriptionRequired || 'Description is required',
    ),
  });

export const ReportIncidentModal: React.FC<ReportIncidentModalProps> = ({
  open,
  onClose,
  projectId,
  projectName,
  onSuccess,
}) => {
  const t = useTranslation('incidents');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const isProjectFixed = !!projectId;

  const { data: projects = [] } = useProjects();
  const { createIncident, isSubmitting } = useCreateIncident();

  const incidentTypeOptions: SelectOption[] = useMemo(
    () => buildIncidentTypeOptions(t),
    [t],
  );

  const delayUnitOptions: SelectOption[] = useMemo(
    () => buildDelayUnitOptions(t),
    [t],
  );

  const initialValues: ReportIncidentFormValues = {
    projectId: projectId || '',
    name: '',
    incidentTime: toLocalInputValue(new Date()),
    incidentType: '',
    otherIncidentType: '',
    delay: '0',
    delayUnitOfMeasure: '',
    description: '',
    taskIds: [],
  };

  const validationSchema = useMemo(
    () => buildValidationSchema(!isProjectFixed, t),
    [isProjectFixed, t],
  );

  const formik = useFormik<ReportIncidentFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await createIncident({
          name: values.name,
          projectId: values.projectId,
          incidentTime: parseLocalInputToIso(values.incidentTime),
          incidentType: values.incidentType || undefined,
          otherIncidentType: values.otherIncidentType || undefined,
          delay: values.delay !== '' ? Number(values.delay) : undefined,
          delayUnitOfMeasure: values.delayUnitOfMeasure || undefined,
          description: values.description,
          taskIds: values.taskIds,
          files: selectedFiles,
        });
        handleClose();
        onSuccess?.();
      } catch (error) {
        // Error toast already shown by useCreateIncident; keep the modal open with the
        // user's entered values intact so nothing is lost.
      }
    },
  });

  const { tasks: projectTasks = [] } = useTasks(
    formik.values.projectId || undefined,
  );

  const handleClose = () => {
    if (isSubmitting) return;
    formik.resetForm();
    setSelectedFiles([]);
    onClose();
  };

  const projectOptions: SelectOption[] = useMemo(
    () =>
      projects.map((project: any) => ({
        value: project.projectId,
        label: project.name,
      })),
    [projects],
  );

  const taskOptions: SelectOption[] = useMemo(
    () =>
      projectTasks.map((task: any) => ({
        value: task.taskId,
        label: task.name,
      })),
    [projectTasks],
  );

  const selectedTaskOptions = taskOptions.filter((option) =>
    formik.values.taskIds.includes(String(option.value)),
  );

  const removeAttachment = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const actionButtons = (
    <>
      <Button
        size="md"
        variant="tertiary"
        type="button"
        onClick={handleClose}
        disabled={isSubmitting}
        leftIcon={<X className="w-5 h-5" />}
        data-testid="report-incident-cancel-button">
        {t?.cancel || 'Cancel'}
      </Button>
      <Button
        size="md"
        variant="primary"
        type="button"
        onClick={() => formik.handleSubmit()}
        loading={isSubmitting}
        disabled={isSubmitting}
        data-testid="report-incident-submit-button">
        {isSubmitting ? t?.submitting || 'Reporting...' : t?.submit || 'Report Incident'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title={t?.modalTitle || 'Report Incident'}
      size="large"
      showCloseButton
      closeOnEscape
      closeOnOverlayClick
      actionButtons={actionButtons}>
      <div data-testid="report-incident-modal">
        <div className="flex items-center gap-2 mb-4">
          <ReportProblemRoundedIcon className="text-2xl text-amber-500" />
        </div>
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
          {isProjectFixed ? (
            <FormGroup label={t?.project || 'Project'} htmlFor="report-incident-project">
              <div className="block w-full rounded-md border border-gray-200 bg-gray-50 p-2 text-sm text-gray-700">
                {projectName}
              </div>
            </FormGroup>
          ) : (
            <FormGroup
              label={t?.project || 'Project'}
              htmlFor="report-incident-project"
              error={
                formik.touched.projectId && formik.errors.projectId
                  ? formik.errors.projectId
                  : undefined
              }>
              <div data-testid="report-incident-project-select">
                <ReactSelect
                  inputId="report-incident-project"
                  options={projectOptions}
                  value={
                    projectOptions.find(
                      (option) => option.value === formik.values.projectId,
                    ) || null
                  }
                  onChange={(option: any) => {
                    formik.setFieldValue('projectId', option?.value || '');
                    formik.setFieldValue('taskIds', []);
                  }}
                  onBlur={() => formik.setFieldTouched('projectId', true)}
                  placeholder={t?.selectProject || 'Select a project...'}
                  error={!!(formik.touched.projectId && formik.errors.projectId)}
                />
              </div>
            </FormGroup>
          )}

          <FormGroup
            label={t?.name || 'Incident Name'}
            htmlFor="report-incident-name"
            error={
              formik.touched.name && formik.errors.name ? formik.errors.name : undefined
            }>
            <Input
              id="report-incident-name"
              name="name"
              data-testid="report-incident-name-input"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={t?.namePlaceholder || 'Enter incident name'}
              disabled={isSubmitting}
            />
          </FormGroup>

          <FormGroup
            label={t?.time || 'Time of Incident'}
            htmlFor="report-incident-time"
            error={
              formik.touched.incidentTime && formik.errors.incidentTime
                ? formik.errors.incidentTime
                : undefined
            }>
            <Input
              id="report-incident-time"
              name="incidentTime"
              type="datetime-local"
              data-testid="report-incident-time-input"
              value={formik.values.incidentTime}
              max={toLocalInputValue(new Date())}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isSubmitting}
            />
          </FormGroup>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup
              label={t?.type || 'Incident Type'}
              htmlFor="report-incident-type"
              error={
                formik.touched.incidentType && formik.errors.incidentType
                  ? formik.errors.incidentType
                  : undefined
              }>
              <div data-testid="report-incident-type-select">
                <ReactSelect
                  inputId="report-incident-type"
                  options={incidentTypeOptions}
                  value={
                    incidentTypeOptions.find(
                      (option) => option.value === formik.values.incidentType,
                    ) || null
                  }
                  onChange={(option: any) =>
                    formik.setFieldValue('incidentType', option?.value || '')
                  }
                  onBlur={() => formik.setFieldTouched('incidentType', true)}
                  placeholder={t?.selectType || 'Select type...'}
                  error={!!(formik.touched.incidentType && formik.errors.incidentType)}
                  isDisabled={isSubmitting}
                />
              </div>
            </FormGroup>

            {formik.values.incidentType === IncidentType.OTHER && (
              <FormGroup
                label={t?.otherType || 'Other Type'}
                htmlFor="report-incident-other-type"
                error={
                  formik.touched.otherIncidentType && formik.errors.otherIncidentType
                    ? formik.errors.otherIncidentType
                    : undefined
                }>
                <Input
                  id="report-incident-other-type"
                  name="otherIncidentType"
                  data-testid="report-incident-other-type-input"
                  value={formik.values.otherIncidentType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t?.otherTypePlaceholder || 'Enter incident type'}
                  disabled={isSubmitting}
                />
              </FormGroup>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup
              label={t?.delay || 'Delay'}
              htmlFor="report-incident-delay"
              error={
                formik.touched.delay && formik.errors.delay
                  ? formik.errors.delay
                  : undefined
              }>
              <Input
                id="report-incident-delay"
                name="delay"
                type="number"
                min={0}
                data-testid="report-incident-delay-input"
                value={formik.values.delay}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isSubmitting}
              />
            </FormGroup>

            <FormGroup
              label={t?.delayUnit || 'Delay Unit'}
              htmlFor="report-incident-delay-unit"
              error={
                formik.touched.delayUnitOfMeasure && formik.errors.delayUnitOfMeasure
                  ? formik.errors.delayUnitOfMeasure
                  : undefined
              }>
              <div data-testid="report-incident-delay-unit-select">
                <ReactSelect
                  inputId="report-incident-delay-unit"
                  options={delayUnitOptions}
                  value={
                    delayUnitOptions.find(
                      (option) => option.value === formik.values.delayUnitOfMeasure,
                    ) || null
                  }
                  onChange={(option: any) =>
                    formik.setFieldValue('delayUnitOfMeasure', option?.value || '')
                  }
                  onBlur={() => formik.setFieldTouched('delayUnitOfMeasure', true)}
                  placeholder={t?.selectUnit || 'Select unit...'}
                  error={
                    !!(formik.touched.delayUnitOfMeasure && formik.errors.delayUnitOfMeasure)
                  }
                  isDisabled={isSubmitting}
                />
              </div>
            </FormGroup>
          </div>

          <FormGroup
            label={t?.description || 'Description'}
            htmlFor="report-incident-description"
            error={
              formik.touched.description && formik.errors.description
                ? formik.errors.description
                : undefined
            }>
            <TextareaInput
              id="report-incident-description"
              name="description"
              data-testid="report-incident-description-input"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={t?.descriptionPlaceholder || 'Describe the incident...'}
              disabled={isSubmitting}
              rows={4}
            />
          </FormGroup>

          <FormGroup
            label={t?.affectedTasks || 'Affected Tasks (optional)'}
            htmlFor="report-incident-tasks">
            <div data-testid="report-incident-tasks-select">
              <ReactSelect
                inputId="report-incident-tasks"
                isMulti
                options={taskOptions}
                value={selectedTaskOptions}
                onChange={(options: any) =>
                  formik.setFieldValue(
                    'taskIds',
                    (options || []).map((option: SelectOption) => String(option.value)),
                  )
                }
                placeholder={
                  formik.values.projectId
                    ? t?.affectedTasksPlaceholder || 'Search and select tasks...'
                    : t?.selectProjectFirst || 'Select a project first'
                }
                isDisabled={!formik.values.projectId || isSubmitting}
              />
            </div>
          </FormGroup>

          <FormGroup
            label={t?.attachments || 'Attachments (optional)'}
            htmlFor="report-incident-attachments">
            <div data-testid="report-incident-attachments-dropzone">
              <FileDropZone
                files={selectedFiles}
                onFilesChange={setSelectedFiles}
                disabled={isSubmitting}
                showFileList={false}
                activeColor="indigo"
              />
            </div>
            {selectedFiles.length > 0 && (
              <ul className="mt-2 flex flex-col gap-1">
                {selectedFiles.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700">
                    <span className="truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      disabled={isSubmitting}
                      data-testid={`report-incident-attachment-remove-${index}`}
                      className="ml-2 text-gray-400 hover:text-gray-700">
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </FormGroup>
        </form>
      </div>
    </Modal>
  );
};

export default ReportIncidentModal;
